// Finds links in free text (circulars, notice boards, timeline posts) so they can be shown as links, and tells what
// kind of page a link points to so the UI can show a video / image / preview card instead of a bare URL.

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
// Punctuation that usually ends a sentence rather than the link ("see https://x.com/a.").
const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/;

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const VIDEO_EXT = /\.(mp4|webm|ogg|mov|m4v)$/i;

const CLOSING_TO_OPENING = { ')': '(', ']': '[', '}': '{' };

const stripTrailing = raw => {
  let url = raw.replace(TRAILING_PUNCTUATION, '');
  // A closing bracket belongs to the link only when the link itself opened one (wikipedia-style urls).
  while (CLOSING_TO_OPENING[url[url.length - 1]]) {
    const close = url[url.length - 1];
    const opens = url.split(CLOSING_TO_OPENING[close]).length - 1;
    const closes = url.split(close).length - 1;
    if (opens >= closes) break;
    url = url.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
  }
  return url;
};

export const toHref = url => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// 'Hello https://a.com, bye' -> [{ type: 'text', value: 'Hello ' }, { type: 'link', value: 'https://a.com', href }, ...]
export const splitLinks = text => {
  const source = typeof text === 'string' ? text : '';
  const parts = [];
  let last = 0;
  source.replace(URL_REGEX, (match, _group, offset) => {
    const url = stripTrailing(match);
    if (!url) return match;
    if (offset > last) parts.push({ type: 'text', value: source.slice(last, offset) });
    parts.push({ type: 'link', value: url, href: toHref(url) });
    last = offset + url.length;
    return match;
  });
  if (last < source.length) parts.push({ type: 'text', value: source.slice(last) });
  return parts;
};

export const extractLinks = text => splitLinks(text).filter(part => part.type === 'link');

const parseUrl = href => {
  try {
    return new URL(toHref(href));
  } catch (e) {
    return null;
  }
};

const youtubeId = url => {
  const host = url.hostname.replace(/^www\.|^m\./, '');
  const segments = url.pathname.split('/').filter(Boolean);
  let id = null;
  if (host === 'youtu.be') id = segments[0];
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (segments[0] === 'watch') id = url.searchParams.get('v');
    else if (['shorts', 'embed', 'live', 'v'].includes(segments[0])) id = segments[1];
  }
  return id && /^[\w-]{6,15}$/.test(id) ? id : null;
};

// -> { kind: 'youtube' | 'instagram' | 'facebook' | 'image' | 'video' | 'link', host, label, embedUrl?, thumbnail? }
export const classifyLink = href => {
  const url = parseUrl(href);
  if (!url) return { kind: 'link', host: '', label: href };
  const host = url.hostname.replace(/^www\./, '');
  const path = url.pathname;

  const ytId = youtubeId(url);
  if (ytId) {
    return {
      kind: 'youtube',
      host,
      label: 'YouTube video',
      embedUrl: `https://www.youtube-nocookie.com/embed/${ytId}`,
      thumbnail: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`,
    };
  }
  if (/(^|\.)instagram\.com$/.test(host)) {
    const match = path.match(/^\/(?:[\w.]+\/)?(p|reel|reels|tv)\/([\w-]+)/);
    return {
      kind: 'instagram',
      host,
      label: match && match[1] !== 'p' ? 'Instagram reel' : 'Instagram post',
      embedUrl: match
        ? `https://www.instagram.com/${match[1] === 'reels' ? 'reel' : match[1]}/${match[2]}/embed`
        : null,
    };
  }
  if (/(^|\.)facebook\.com$/.test(host) || host === 'fb.watch' || host === 'fb.com') {
    const isVideo = host === 'fb.watch' || /\/(videos?|reel|watch)\b/.test(path);
    return {
      kind: 'facebook',
      host,
      label: isVideo ? 'Facebook video' : 'Facebook post',
      embedUrl: `https://www.facebook.com/plugins/${isVideo ? 'video' : 'post'}.php?href=${encodeURIComponent(
        url.toString()
      )}&show_text=false`,
    };
  }
  if (IMAGE_EXT.test(path)) return { kind: 'image', host, label: 'Image' };
  if (VIDEO_EXT.test(path)) return { kind: 'video', host, label: 'Video' };
  return { kind: 'link', host, label: host };
};
